import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, EyeOff, Mail, Phone, MessageCircle, CheckCircle } from 'lucide-react';
import { apiUrl } from '../../utils/api';
import { Commitment } from '../../hooks/useCampaignDetail';
import { generateWhatsAppLink } from '../../utils/date';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface Props {
  commitments: Commitment[];
  setCommitments: (c: Commitment[]) => void;
  campaignTitle: string;
  onReload: () => void;
  onError: (msg: string) => void;
}

export function CommitmentsTable({ commitments, setCommitments, campaignTitle, onReload, onError }: Props) {
  const navigate = useNavigate();
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const getToken = () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return null; }
    return token;
  };

  const handleApprove = async (id: string) => {
    const token = getToken(); if (!token) return;
    try {
      const res = await fetch(apiUrl(`/api/commitments/${id}/status`), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: 'verified' }),
      });
      const data = await res.json();
      if (data.success) {
        setCommitments(commitments.map(c => c.id === id ? { ...c, status: 'verified' } : c));
        onReload();
      } else onError(data.error?.message || 'Error');
    } catch { onError('Error de conexión'); }
  };

  const handleDeliver = async (id: string) => {
    const token = getToken(); if (!token) return;
    try {
      const res = await fetch(apiUrl(`/api/commitments/${id}/status`), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: 'delivered' }),
      });
      const data = await res.json();
      if (data.success) {
        setCommitments(commitments.map(c => c.id === id ? { ...c, status: 'delivered' } : c));
        onReload();
      } else onError(data.error?.message || 'Error');
    } catch { onError('Error de conexión'); }
  };

  const handleRejectConfirm = async (reason?: string) => {
    if (!rejectTarget || !reason) return;
    const token = getToken(); if (!token) return;
    try {
      const res = await fetch(apiUrl(`/api/commitments/${rejectTarget}`), {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setCommitments(commitments.filter(c => c.id !== rejectTarget));
        onReload();
      } else onError(data.error?.message || 'Error');
    } catch { onError('Error de conexión'); }
    finally { setRejectTarget(null); }
  };

  if (commitments.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <Users className="mx-auto h-10 w-10 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">No hay compromisos aún.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Donantes ({commitments.length})</h2>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Donante</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Productos</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {commitments.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{c.donorName}</p>
                  {c.isAnonymous && (
                    <span className="text-xs text-gray-500 flex items-center mt-0.5"><EyeOff className="w-3 h-3 mr-1" />Anónimo</span>
                  )}
                  <p className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleDateString('es-ES')}</p>
                </td>
                <td className="px-4 py-3">
                  {c.items.map((item, i) => (
                    <p key={i} className="text-gray-700"><span className="font-medium">{item.quantity} {item.unit}</span> de {item.productName}</p>
                  ))}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center text-gray-700 mb-1"><Mail className="w-3 h-3 mr-1 text-gray-400" />{c.donorEmail}</div>
                  <div className="flex items-center text-gray-700">
                    <Phone className="w-3 h-3 mr-1 text-gray-400" />{c.donorPhone}
                    <a href={generateWhatsAppLink(c.donorPhone, campaignTitle)} target="_blank" rel="noopener noreferrer"
                      className="ml-2 text-green-600 hover:text-green-800"><MessageCircle className="w-4 h-4" /></a>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    c.status === 'delivered' ? 'bg-green-100 text-green-800' :
                    c.status === 'verified' ? 'bg-blue-100 text-blue-800' :
                    c.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {c.status === 'delivered' ? 'Recibido' : c.status === 'verified' ? 'Verificado' : c.status === 'pending' ? 'Pendiente' : 'Cancelado'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-1">
                    {c.status === 'pending' && (
                      <>
                        <button onClick={() => handleApprove(c.id)} className="text-xs text-green-700 border border-green-300 px-2 py-1 rounded hover:bg-green-50 flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />Verificar
                        </button>
                        <button onClick={() => setRejectTarget(c.id)} className="text-xs text-red-700 border border-red-300 px-2 py-1 rounded hover:bg-red-50">✕</button>
                      </>
                    )}
                    {c.status === 'verified' && (
                      <>
                        <button onClick={() => handleDeliver(c.id)} className="text-xs text-blue-700 border border-blue-300 px-2 py-1 rounded hover:bg-blue-50 flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />Confirmar Recepción
                        </button>
                        <button onClick={() => setRejectTarget(c.id)} className="text-xs text-red-700 border border-red-300 px-2 py-1 rounded hover:bg-red-50">✕</button>
                      </>
                    )}
                    {c.status === 'delivered' && <span className="text-xs text-green-600">✓ Recibido</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100">
        {commitments.map((c) => (
          <div key={c.id} className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 text-sm">{c.donorName}</p>
                {c.isAnonymous && <span className="text-xs text-gray-500 flex items-center"><EyeOff className="w-3 h-3 mr-1" />Anónimo</span>}
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                c.status === 'delivered' ? 'bg-green-100 text-green-800' :
                c.status === 'verified' ? 'bg-blue-100 text-blue-800' :
                c.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              }`}>
                {c.status === 'delivered' ? 'Recibido' : c.status === 'verified' ? 'Verificado' : c.status === 'pending' ? 'Pendiente' : 'Cancelado'}
              </span>
            </div>

            <div className="text-sm text-gray-700">
              {c.items.map((item, i) => (
                <p key={i}><span className="font-medium">{item.quantity} {item.unit}</span> de {item.productName}</p>
              ))}
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
              <span className="flex items-center"><Mail className="w-3 h-3 mr-1" />{c.donorEmail}</span>
              <span className="flex items-center"><Phone className="w-3 h-3 mr-1" />{c.donorPhone}</span>
              <a href={generateWhatsAppLink(c.donorPhone, campaignTitle)} target="_blank" rel="noopener noreferrer"
                className="flex items-center text-green-600"><MessageCircle className="w-3.5 h-3.5 mr-1" />WhatsApp</a>
            </div>

            <div className="flex items-center gap-2">
              {c.status === 'pending' && (
                <>
                  <button onClick={() => handleApprove(c.id)} className="text-xs text-green-700 border border-green-300 px-2.5 py-1.5 rounded hover:bg-green-50 flex items-center flex-1 justify-center">
                    <CheckCircle className="w-3 h-3 mr-1" />Verificar
                  </button>
                  <button onClick={() => setRejectTarget(c.id)} className="text-xs text-red-700 border border-red-300 px-2.5 py-1.5 rounded hover:bg-red-50">✕ Rechazar</button>
                </>
              )}
              {c.status === 'verified' && (
                <>
                  <button onClick={() => handleDeliver(c.id)} className="text-xs text-blue-700 border border-blue-300 px-2.5 py-1.5 rounded hover:bg-blue-50 flex items-center flex-1 justify-center">
                    <CheckCircle className="w-3 h-3 mr-1" />Confirmar Recepción
                  </button>
                  <button onClick={() => setRejectTarget(c.id)} className="text-xs text-red-700 border border-red-300 px-2.5 py-1.5 rounded hover:bg-red-50">✕</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!rejectTarget}
        title="Eliminar aporte"
        message="Esta acción no se puede deshacer. El donante será notificado."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        textInput={{
          label: 'Razón del rechazo *',
          placeholder: 'Explica por qué eliminas este aporte...',
          minLength: 10,
        }}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectTarget(null)}
      />
    </div>
  );
}
